// Types.
import type event from '../Types/Event';
import type source from '../Types/Source';

// Classes.
import Handler from "./Handler";

declare global {
    interface Window {
        _globalScrollyObserver: Observer;
    }
}

type ObserverTarget = {
    target :HTMLElement|null,
    callback :Function,
};

// TODO: Use this new (not yet finished or tested) global intersection observer for PROGESS as well.

/**
 * Scrolly.Observer
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-09-02 Added unobserve/removeTarget methods and bug fix.
 * @package fk_js/scrolly
 * @version: 1.0.2
 */
class Observer
{

    private targets :Set<ObserverTarget> = new Set();
    private intersectionObserver :IntersectionObserver;

    /**
     * Constructor.
     */
    constructor()
    {
        this.intersectionObserver = new IntersectionObserver((entries :IntersectionObserverEntry[]) :void => {
            entries.forEach((entry :IntersectionObserverEntry) :void => {
                for (const target of this.targets) {
                    if (target.target === entry.target) {
                        target.callback(entry);
                    }
                }
                // TODO: Iterate over all subscribed handlers and call their intersectionObserver handlers ...
            });
        });
    }

    // ###############
    // Private methods
    // ###############



    // ##############
    // Public methods
    // ##############

    /**
     * Add an observer target.
     */
    public addTarget(target :ObserverTarget) :Observer
    {
        if (!target.target){
            return this;
        }
        this.targets.add(target);
        this.intersectionObserver.observe(target.target);
        return this;
    }

    /**
     * Observe an element using the global intersection observer.
     */
    public observe(target :HTMLElement, callback :Function) :Observer
    {
        Observer.init();
        window._globalScrollyObserver.addTarget({
            target: target,
            callback: callback,
        });
        return window._globalScrollyObserver;
    }

    /**
     * Remove an observer target.
     */
    public removeTarget(target :HTMLElement) :Observer
    {
        // TODO: also find HTMLElement in this.targets and remove it.
        this.intersectionObserver.unobserve(target);
        return this;
    }

    /**
     * Unobserve an element using the global intersection observer.
     */
    public unobserve(target :HTMLElement) :Observer
    {
        Observer.init();
        window._globalScrollyObserver.removeTarget(target);
        return window._globalScrollyObserver;
    }

    // ##############
    // Static methods
    // ##############

    /**
     * Initialize the global scroll intersection observer.
     */
    static init() :Observer
    {
        if (!window._globalScrollyObserver) {
            window._globalScrollyObserver = new Observer();
        }
        return window._globalScrollyObserver;
    }

}

export default Observer;